import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type UserVideo, requestVideoDelete, deleteVideoAPI } from '@/api/videoApi';
import { type UserImage, requestImageDelete, deleteImage } from '@/api/imageApi';
import videoKeys from '@/api/querry_keys/videoKeys';
import imageKeys from '@/api/querry_keys/imageKeys';
import StatusBadge from '@/components/ui/common/StatusBadge';
import { createFavouriteSuccessCallback, createArchiveSuccessCallback } from '@/utils/common/toastCallbacks';

/**
 * Shared hook for handling media (video/image) page operations
 * Used by FavouritesPage, ArchivesPage, and similar pages
 */
export const useMediaPageHandlers = (
  userEmail: string | null,
  refetchVideos: () => void,
  refetchImages: () => void,
  invalidationKeys?: {
    videos?: any[];
    images?: any[];
  }
) => {
  const queryClient = useQueryClient();

  // Modal state
  const [selectedVideo, setSelectedVideo] = useState<UserVideo | null>(null);
  const [selectedImage, setSelectedImage] = useState<UserImage | null>(null);

  // Toast state for favourite notifications
  const [favouriteToastOpen, setFavouriteToastOpen] = useState(false);
  const [favouriteToastMessage, setFavouriteToastMessage] = useState('');

  // Toast state for archive notifications
  const [archiveToastOpen, setArchiveToastOpen] = useState(false);
  const [archiveToastMessage, setArchiveToastMessage] = useState('');

  /**
   * Handle video click - open video modal
   */
  const handleVideoClick = useCallback((video: UserVideo) => {
    setSelectedVideo(video);
  }, []);

  /**
   * Handle image click - open image modal
   */
  const handleImageClick = useCallback((image: UserImage) => {
    setSelectedImage(image);
  }, []);

  /**
   * Get status badge for content
   */
  const getStatusBadge = useCallback((status?: string) => {
    return <StatusBadge status={status} />;
  }, []);

  /**
   * Handle favourite toggle success - show toast notification
   */
  const handleFavouriteSuccess = useCallback(
    createFavouriteSuccessCallback(setFavouriteToastMessage, setFavouriteToastOpen),
    []
  );

  /**
   * Handle archive toggle success - show toast notification
   */
  const handleArchiveSuccess = useCallback(
    createArchiveSuccessCallback(setArchiveToastMessage, setArchiveToastOpen),
    []
  );

  /**
   * Format file size for display
   */
  const formatFileSize = useCallback((bytes: number) => {
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }, []);

  /**
   * Format date for display
   */
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  }, []);

  /**
   * Handle video delete request (for non-admin users)
   */
  const handleRequestDelete = useCallback(async (videoId: string) => {
    try {
      await requestVideoDelete(videoId);

      // Update the selected video state to reflect the delete request
      if (selectedVideo?.id === videoId) {
        setSelectedVideo({
          ...selectedVideo,
          delete_request: true
        });
      }

      // Invalidate and refetch
      const keysToInvalidate = invalidationKeys?.videos || [videoKeys.all];
      for (const key of keysToInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      refetchVideos();
    } catch (error) {
      console.error('Failed to request video deletion:', error);

      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Video not found')) {
        alert('This video no longer exists. The page will refresh.');
        setSelectedVideo(null);
        const keysToInvalidate = invalidationKeys?.videos || [videoKeys.all];
        for (const key of keysToInvalidate) {
          queryClient.invalidateQueries({ queryKey: key });
        }
        refetchVideos();
      } else {
        alert('Failed to request video deletion. Please try again.');
      }
      throw error;
    }
  }, [selectedVideo, queryClient, refetchVideos, invalidationKeys]);

  /**
   * Handle image delete request (for non-admin users)
   */
  const handleRequestImageDelete = useCallback(async (imageId: string) => {
    try {
      await requestImageDelete(imageId);

      // Update the selected image state to reflect the delete request
      if (selectedImage?.id === imageId) {
        setSelectedImage({
          ...selectedImage,
          delete_request: true
        });
      }

      // Invalidate and refetch
      const keysToInvalidate = invalidationKeys?.images || [imageKeys.all];
      for (const key of keysToInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      refetchImages();
    } catch (error) {
      console.error('Failed to request image deletion:', error);

      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Image not found') || errorMessage.includes('not found')) {
        alert('This image no longer exists. The page will refresh.');
        setSelectedImage(null);
        const keysToInvalidate = invalidationKeys?.images || [imageKeys.all];
        for (const key of keysToInvalidate) {
          queryClient.invalidateQueries({ queryKey: key });
        }
        refetchImages();
      } else {
        alert('Failed to request image deletion. Please try again.');
      }
      throw error;
    }
  }, [selectedImage, queryClient, refetchImages, invalidationKeys]);

  /**
   * Handle video delete (for admin users)
   */
  const handleDeleteVideo = useCallback(async (videoId: string) => {
    if (videoId !== selectedVideo?.id) {
      return;
    }

    try {
      await deleteVideoAPI(videoId, userEmail);

      // Close modal and refresh list
      setSelectedVideo(null);
      const keysToInvalidate = invalidationKeys?.videos || [videoKeys.all];
      for (const key of keysToInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      refetchVideos();

      alert('Video deleted successfully');
    } catch (error) {
      console.error('Failed to delete video:', error);
      alert(`Failed to delete video: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [selectedVideo, userEmail, queryClient, refetchVideos, invalidationKeys]);

  /**
   * Handle image delete (for admin users)
   */
  const handleDeleteImage = useCallback(async (imageId: string) => {
    if (imageId !== selectedImage?.id) {
      return;
    }

    try {
      await deleteImage(imageId);

      // Close modal and refresh list
      setSelectedImage(null);
      const keysToInvalidate = invalidationKeys?.images || [imageKeys.all];
      for (const key of keysToInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      refetchImages();

      alert('Image deleted successfully');
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert(`Failed to delete image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [selectedImage, queryClient, refetchImages, invalidationKeys]);

  /**
   * Close modals
   */
  const closeModals = useCallback(() => {
    setSelectedVideo(null);
    setSelectedImage(null);
  }, []);

  return {
    // State
    selectedVideo,
    selectedImage,
    favouriteToastOpen,
    favouriteToastMessage,
    archiveToastOpen,
    archiveToastMessage,

    // Setters
    setSelectedVideo,
    setSelectedImage,
    setFavouriteToastOpen,
    setArchiveToastOpen,

    // Handlers
    handleVideoClick,
    handleImageClick,
    getStatusBadge,
    handleFavouriteSuccess,
    handleArchiveSuccess,
    formatFileSize,
    formatDate,
    handleRequestDelete,
    handleRequestImageDelete,
    handleDeleteVideo,
    handleDeleteImage,
    closeModals,
  };
};
